/**
 * ════════════════════════════════════════════════════════════════════════════
 * VEDIC CALCULATOR LOGIC
 * 吠陀占星计算引擎
 * ════════════════════════════════════════════════════════════════════════════
 * 
 * 依赖：astronomy.browser.min.js
 * 
 * 功能：
 * - 岁差校正 (Ayanamsa)
 * - 恒星黄道计算
 * - 27 月宿 (Nakshatra)
 * - 大运周期 (Vimshottari Dasha)
 * - 婚配匹配 (Gun Milan)
 * - 火星缺陷检测 (Manglik Dosha)
 * - 补救措施 (Remedies)
 */

const VedicCalculator = {

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：12 星座
  // ════════════════════════════════════════════════════════════════════════════

  SIGNS: [
    { index: 0,  nameEn: 'Aries',       nameCn: '白羊座', nameSanskrit: 'Mesha',      symbol: '♈', element: 'Fire',  lord: 'Mars' },
    { index: 1,  nameEn: 'Taurus',      nameCn: '金牛座', nameSanskrit: 'Vrishabha',  symbol: '♉', element: 'Earth', lord: 'Venus' },
    { index: 2,  nameEn: 'Gemini',      nameCn: '双子座', nameSanskrit: 'Mithuna',    symbol: '♊', element: 'Air',   lord: 'Mercury' },
    { index: 3,  nameEn: 'Cancer',      nameCn: '巨蟹座', nameSanskrit: 'Karka',      symbol: '♋', element: 'Water', lord: 'Moon' },
    { index: 4,  nameEn: 'Leo',         nameCn: '狮子座', nameSanskrit: 'Simha',      symbol: '♌', element: 'Fire',  lord: 'Sun' },
    { index: 5,  nameEn: 'Virgo',       nameCn: '处女座', nameSanskrit: 'Kanya',      symbol: '♍', element: 'Earth', lord: 'Mercury' },
    { index: 6,  nameEn: 'Libra',       nameCn: '天秤座', nameSanskrit: 'Tula',       symbol: '♎', element: 'Air',   lord: 'Venus' },
    { index: 7,  nameEn: 'Scorpio',     nameCn: '天蝎座', nameSanskrit: 'Vrishchika', symbol: '♏', element: 'Water', lord: 'Mars' },
    { index: 8,  nameEn: 'Sagittarius', nameCn: '射手座', nameSanskrit: 'Dhanu',      symbol: '♐', element: 'Fire',  lord: 'Jupiter' },
    { index: 9,  nameEn: 'Capricorn',   nameCn: '摩羯座', nameSanskrit: 'Makara',     symbol: '♑', element: 'Earth', lord: 'Saturn' },
    { index: 10, nameEn: 'Aquarius',    nameCn: '水瓶座', nameSanskrit: 'Kumbha',     symbol: '♒', element: 'Air',   lord: 'Saturn' },
    { index: 11, nameEn: 'Pisces',      nameCn: '双鱼座', nameSanskrit: 'Meena',      symbol: '♓', element: 'Water', lord: 'Jupiter' }
  ],

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：27 月宿 (Nakshatras)
  // ════════════════════════════════════════════════════════════════════════════

  NAKSHATRAS: [
    { index: 0,  name: 'Ashwini',         nameCn: '马宿',     lord: 'Ketu',    deity: 'Ashwini Kumaras', symbol: 'Horse Head',           gana: 'Deva',   yoni: 'Horse',    nadi: 'Vata',   varna: 'Vaishya' },
    { index: 1,  name: 'Bharani',         nameCn: '壁宿',     lord: 'Venus',   deity: 'Yama',            symbol: 'Yoni',                 gana: 'Manush', yoni: 'Elephant', nadi: 'Pitta',  varna: 'Mleccha' },
    { index: 2,  name: 'Krittika',        nameCn: '昴宿',     lord: 'Sun',     deity: 'Agni',            symbol: 'Razor',                gana: 'Raksh',  yoni: 'Sheep',    nadi: 'Kapha',  varna: 'Brahmin' },
    { index: 3,  name: 'Rohini',          nameCn: '毕宿',     lord: 'Moon',    deity: 'Brahma',          symbol: 'Cart',                 gana: 'Manush', yoni: 'Serpent',  nadi: 'Kapha',  varna: 'Shudra' },
    { index: 4,  name: 'Mrigashira',      nameCn: '参宿',     lord: 'Mars',    deity: 'Soma',            symbol: 'Deer Head',            gana: 'Deva',   yoni: 'Serpent',  nadi: 'Pitta',  varna: 'Vaishya' },
    { index: 5,  name: 'Ardra',           nameCn: '井宿',     lord: 'Rahu',    deity: 'Rudra',           symbol: 'Teardrop',             gana: 'Manush', yoni: 'Dog',      nadi: 'Vata',   varna: 'Mleccha' },
    { index: 6,  name: 'Punarvasu',       nameCn: '鬼宿',     lord: 'Jupiter', deity: 'Aditi',           symbol: 'Bow',                  gana: 'Deva',   yoni: 'Cat',      nadi: 'Vata',   varna: 'Vaishya' },
    { index: 7,  name: 'Pushya',          nameCn: '柳宿',     lord: 'Saturn',  deity: 'Brihaspati',      symbol: 'Flower',               gana: 'Deva',   yoni: 'Sheep',    nadi: 'Pitta',  varna: 'Kshatriya' },
    { index: 8,  name: 'Ashlesha',        nameCn: '星宿',     lord: 'Mercury', deity: 'Sarpa',           symbol: 'Serpent',              gana: 'Raksh',  yoni: 'Cat',      nadi: 'Kapha',  varna: 'Mleccha' },
    { index: 9,  name: 'Magha',           nameCn: '张宿',     lord: 'Ketu',    deity: 'Pitris',          symbol: 'Throne',               gana: 'Raksh',  yoni: 'Rat',      nadi: 'Kapha',  varna: 'Shudra' },
    { index: 10, name: 'Purva Phalguni',  nameCn: '翼宿',     lord: 'Venus',   deity: 'Bhaga',           symbol: 'Hammock',              gana: 'Manush', yoni: 'Rat',      nadi: 'Pitta',  varna: 'Brahmin' },
    { index: 11, name: 'Uttara Phalguni', nameCn: '轸宿',     lord: 'Sun',     deity: 'Aryaman',         symbol: 'Bed',                  gana: 'Manush', yoni: 'Cow',      nadi: 'Vata',   varna: 'Kshatriya' },
    { index: 12, name: 'Hasta',           nameCn: '角宿',     lord: 'Moon',    deity: 'Savitar',         symbol: 'Hand',                 gana: 'Deva',   yoni: 'Buffalo',  nadi: 'Vata',   varna: 'Vaishya' },
    { index: 13, name: 'Chitra',          nameCn: '亢宿',     lord: 'Mars',    deity: 'Vishvakarma',     symbol: 'Pearl',                gana: 'Raksh',  yoni: 'Tiger',    nadi: 'Pitta',  varna: 'Mleccha' },
    { index: 14, name: 'Swati',           nameCn: '氐宿',     lord: 'Rahu',    deity: 'Vayu',            symbol: 'Coral',                gana: 'Deva',   yoni: 'Buffalo',  nadi: 'Kapha',  varna: 'Mleccha' },
    { index: 15, name: 'Vishakha',        nameCn: '房宿',     lord: 'Jupiter', deity: 'Indragni',        symbol: 'Archway',              gana: 'Raksh',  yoni: 'Tiger',    nadi: 'Kapha',  varna: 'Mleccha' },
    { index: 16, name: 'Anuradha',        nameCn: '心宿',     lord: 'Saturn',  deity: 'Mitra',           symbol: 'Lotus',                gana: 'Deva',   yoni: 'Deer',     nadi: 'Pitta',  varna: 'Shudra' },
    { index: 17, name: 'Jyeshtha',        nameCn: '尾宿',     lord: 'Mercury', deity: 'Indra',           symbol: 'Earring',              gana: 'Raksh',  yoni: 'Deer',     nadi: 'Vata',   varna: 'Vaishya' },
    { index: 18, name: 'Mula',            nameCn: '箕宿',     lord: 'Ketu',    deity: 'Nirrti',          symbol: 'Root',                 gana: 'Raksh',  yoni: 'Dog',      nadi: 'Vata',   varna: 'Mleccha' },
    { index: 19, name: 'Purva Ashadha',   nameCn: '斗宿',     lord: 'Venus',   deity: 'Apas',            symbol: 'Fan',                  gana: 'Manush', yoni: 'Monkey',   nadi: 'Pitta',  varna: 'Brahmin' },
    { index: 20, name: 'Uttara Ashadha',  nameCn: '牛宿',     lord: 'Sun',     deity: 'Vishvadevas',     symbol: 'Tusk',                 gana: 'Manush', yoni: 'Mongoose', nadi: 'Kapha',  varna: 'Kshatriya' },
    { index: 21, name: 'Shravana',        nameCn: '女宿',     lord: 'Moon',    deity: 'Vishnu',          symbol: 'Ear',                  gana: 'Deva',   yoni: 'Monkey',   nadi: 'Kapha',  varna: 'Mleccha' },
    { index: 22, name: 'Dhanishta',       nameCn: '虚宿',     lord: 'Mars',    deity: 'Vasus',           symbol: 'Drum',                 gana: 'Raksh',  yoni: 'Lion',     nadi: 'Pitta',  varna: 'Vaishya' },
    { index: 23, name: 'Shatabhisha',     nameCn: '危宿',     lord: 'Rahu',    deity: 'Varuna',          symbol: 'Circle',               gana: 'Raksh',  yoni: 'Horse',    nadi: 'Vata',   varna: 'Mleccha' },
    { index: 24, name: 'Purva Bhadrapada',nameCn: '室宿',     lord: 'Jupiter', deity: 'Aja Ekapada',     symbol: 'Sword',                gana: 'Manush', yoni: 'Lion',     nadi: 'Vata',   varna: 'Brahmin' },
    { index: 25, name: 'Uttara Bhadrapada',nameCn: '壁宿',    lord: 'Saturn',  deity: 'Ahir Budhnya',    symbol: 'Twins',                gana: 'Manush', yoni: 'Cow',      nadi: 'Pitta',  varna: 'Kshatriya' },
    { index: 26, name: 'Revati',          nameCn: '奎宿',     lord: 'Mercury', deity: 'Pushan',          symbol: 'Fish',                 gana: 'Deva',   yoni: 'Elephant', nadi: 'Kapha',  varna: 'Shudra' }
  ],

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：大运周期 (Dasha Years)
  // ════════════════════════════════════════════════════════════════════════════

  DASHA_SEQUENCE: ['Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury'],
  
  DASHA_YEARS: {
    Ketu: 7, Venus: 20, Sun: 6, Moon: 10, Mars: 7, 
    Rahu: 18, Jupiter: 16, Saturn: 19, Mercury: 17
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：行星数据
  // ════════════════════════════════════════════════════════════════════════════

  PLANETS: {
    Sun:     { nameEn: 'Sun',     nameCn: '太阳', nameSanskrit: 'Surya',   symbol: '☉' },
    Moon:    { nameEn: 'Moon',    nameCn: '月亮', nameSanskrit: 'Chandra', symbol: '☽' },
    Mars:    { nameEn: 'Mars',    nameCn: '火星', nameSanskrit: 'Mangal',  symbol: '♂' },
    Mercury: { nameEn: 'Mercury', nameCn: '水星', nameSanskrit: 'Budha',   symbol: '☿' },
    Jupiter: { nameEn: 'Jupiter', nameCn: '木星', nameSanskrit: 'Guru',    symbol: '♃' },
    Venus:   { nameEn: 'Venus',   nameCn: '金星', nameSanskrit: 'Shukra',  symbol: '♀' },
    Saturn:  { nameEn: 'Saturn',  nameCn: '土星', nameSanskrit: 'Shani',   symbol: '♄' },
    Rahu:    { nameEn: 'Rahu',    nameCn: '罗睺', nameSanskrit: 'Rahu',    symbol: '☊' },
    Ketu:    { nameEn: 'Ketu',    nameCn: '计都', nameSanskrit: 'Ketu',    symbol: '☋' }
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：补救措施 (Remedies)
  // ════════════════════════════════════════════════════════════════════════════

  REMEDIES: {
    Sun: {
      mantra: 'ॐ सूर्याय नमः',
      mantraRoman: 'Om Suryaya Namaha',
      mantraCn: '唵 苏里雅亚 纳玛哈',
      repetitions: 7000,
      gemstone: { nameEn: 'Ruby', nameCn: '红宝石', nameSanskrit: 'Manik' },
      color: { nameEn: 'Red / Orange', nameCn: '红色 / 橙色' },
      day: { nameEn: 'Sunday', nameCn: '周日' },
      metal: { nameEn: 'Gold', nameCn: '黄金' },
      charity: { nameEn: 'Donate wheat, jaggery, or copper on Sundays', nameCn: '周日捐赠小麦、红糖或铜器' },
      fasting: { nameEn: 'Fast on Sundays', nameCn: '周日斋戒' }
    },
    Moon: {
      mantra: 'ॐ चन्द्राय नमः',
      mantraRoman: 'Om Chandraya Namaha',
      mantraCn: '唵 占德拉亚 纳玛哈',
      repetitions: 11000,
      gemstone: { nameEn: 'Pearl', nameCn: '珍珠', nameSanskrit: 'Moti' },
      color: { nameEn: 'White / Silver', nameCn: '白色 / 银色' },
      day: { nameEn: 'Monday', nameCn: '周一' },
      metal: { nameEn: 'Silver', nameCn: '白银' },
      charity: { nameEn: 'Donate rice, milk, or white cloth on Mondays', nameCn: '周一捐赠大米、牛奶或白布' },
      fasting: { nameEn: 'Fast on Mondays', nameCn: '周一斋戒' }
    },
    Mars: {
      mantra: 'ॐ मङ्गलाय नमः',
      mantraRoman: 'Om Mangalaya Namaha',
      mantraCn: '唵 曼伽拉亚 纳玛哈',
      repetitions: 10000,
      gemstone: { nameEn: 'Red Coral', nameCn: '红珊瑚', nameSanskrit: 'Moonga' },
      color: { nameEn: 'Red / Maroon', nameCn: '红色 / 栗色' },
      day: { nameEn: 'Tuesday', nameCn: '周二' },
      metal: { nameEn: 'Copper', nameCn: '铜' },
      charity: { nameEn: 'Donate red lentils, jaggery, or red cloth on Tuesdays', nameCn: '周二捐赠红扁豆、红糖或红布' },
      fasting: { nameEn: 'Fast on Tuesdays', nameCn: '周二斋戒' }
    },
    Mercury: {
      mantra: 'ॐ बुधाय नमः',
      mantraRoman: 'Om Budhaya Namaha',
      mantraCn: '唵 布达亚 纳玛哈',
      repetitions: 9000,
      gemstone: { nameEn: 'Emerald', nameCn: '祖母绿', nameSanskrit: 'Panna' },
      color: { nameEn: 'Green', nameCn: '绿色' },
      day: { nameEn: 'Wednesday', nameCn: '周三' },
      metal: { nameEn: 'Bronze', nameCn: '青铜' },
      charity: { nameEn: 'Donate green vegetables, moong dal on Wednesdays', nameCn: '周三捐赠绿色蔬菜、绿豆' },
      fasting: { nameEn: 'Fast on Wednesdays', nameCn: '周三斋戒' }
    },
    Jupiter: {
      mantra: 'ॐ गुरवे नमः',
      mantraRoman: 'Om Gurave Namaha',
      mantraCn: '唵 古拉维 纳玛哈',
      repetitions: 19000,
      gemstone: { nameEn: 'Yellow Sapphire', nameCn: '黄宝石', nameSanskrit: 'Pukhraj' },
      color: { nameEn: 'Yellow / Gold', nameCn: '黄色 / 金色' },
      day: { nameEn: 'Thursday', nameCn: '周四' },
      metal: { nameEn: 'Gold', nameCn: '黄金' },
      charity: { nameEn: 'Donate turmeric, yellow cloth, bananas on Thursdays', nameCn: '周四捐赠姜黄、黄布、香蕉' },
      fasting: { nameEn: 'Fast on Thursdays', nameCn: '周四斋戒' }
    },
    Venus: {
      mantra: 'ॐ शुक्राय नमः',
      mantraRoman: 'Om Shukraya Namaha',
      mantraCn: '唵 舒克拉亚 纳玛哈',
      repetitions: 16000,
      gemstone: { nameEn: 'Diamond', nameCn: '钻石', nameSanskrit: 'Heera' },
      color: { nameEn: 'White / Pink', nameCn: '白色 / 粉色' },
      day: { nameEn: 'Friday', nameCn: '周五' },
      metal: { nameEn: 'Silver', nameCn: '白银' },
      charity: { nameEn: 'Donate silk, perfume, sugar on Fridays', nameCn: '周五捐赠丝绸、香水、糖' },
      fasting: { nameEn: 'Fast on Fridays', nameCn: '周五斋戒' }
    },
    Saturn: {
      mantra: 'ॐ शनैश्चराय नमः',
      mantraRoman: 'Om Shanaischaraya Namaha',
      mantraCn: '唵 沙奈斯查拉亚 纳玛哈',
      repetitions: 23000,
      gemstone: { nameEn: 'Blue Sapphire', nameCn: '蓝宝石', nameSanskrit: 'Neelam' },
      color: { nameEn: 'Black / Dark Blue', nameCn: '黑色 / 深蓝' },
      day: { nameEn: 'Saturday', nameCn: '周六' },
      metal: { nameEn: 'Iron', nameCn: '铁' },
      charity: { nameEn: 'Donate black sesame, iron, oil on Saturdays', nameCn: '周六捐赠黑芝麻、铁器、油' },
      fasting: { nameEn: 'Fast on Saturdays (before sunset)', nameCn: '周六斋戒（日落前）' }
    },
    Rahu: {
      mantra: 'ॐ राहवे नमः',
      mantraRoman: 'Om Rahave Namaha',
      mantraCn: '唵 拉哈维 纳玛哈',
      repetitions: 18000,
      gemstone: { nameEn: 'Hessonite', nameCn: '桂榴石', nameSanskrit: 'Gomed' },
      color: { nameEn: 'Smoky / Dark Blue', nameCn: '烟灰色 / 深蓝' },
      day: { nameEn: 'Saturday', nameCn: '周六' },
      metal: { nameEn: 'Lead', nameCn: '铅' },
      charity: { nameEn: 'Donate blankets, coal, or black cloth on Saturdays', nameCn: '周六捐赠毯子、煤炭或黑布' },
      fasting: { nameEn: 'Fast on Saturdays', nameCn: '周六斋戒' }
    },
    Ketu: {
      mantra: 'ॐ केतवे नमः',
      mantraRoman: 'Om Ketave Namaha',
      mantraCn: '唵 盖塔维 纳玛哈',
      repetitions: 17000,
      gemstone: { nameEn: "Cat's Eye", nameCn: '猫眼石', nameSanskrit: 'Lehsunia' },
      color: { nameEn: 'Grey / Earthy', nameCn: '灰色 / 土色' },
      day: { nameEn: 'Tuesday', nameCn: '周二' },
      metal: { nameEn: 'Iron', nameCn: '铁' },
      charity: { nameEn: 'Donate blankets, sesame seeds on Tuesdays', nameCn: '周二捐赠毯子、芝麻' },
      fasting: { nameEn: 'Fast on Tuesdays', nameCn: '周二斋戒' }
    }
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 常量：行星友敌关系 (用于 Graha Maitri)
  // ════════════════════════════════════════════════════════════════════════════

  PLANET_RELATIONS: {
    Sun:     { friends: ['Moon', 'Mars', 'Jupiter'], enemies: ['Venus', 'Saturn'], neutral: ['Mercury'] },
    Moon:    { friends: ['Sun', 'Mercury'], enemies: [], neutral: ['Mars', 'Jupiter', 'Venus', 'Saturn'] },
    Mars:    { friends: ['Sun', 'Moon', 'Jupiter'], enemies: ['Mercury'], neutral: ['Venus', 'Saturn'] },
    Mercury: { friends: ['Sun', 'Venus'], enemies: ['Moon'], neutral: ['Mars', 'Jupiter', 'Saturn'] },
    Jupiter: { friends: ['Sun', 'Moon', 'Mars'], enemies: ['Mercury', 'Venus'], neutral: ['Saturn'] },
    Venus:   { friends: ['Mercury', 'Saturn'], enemies: ['Sun', 'Moon'], neutral: ['Mars', 'Jupiter'] },
    Saturn:  { friends: ['Mercury', 'Venus'], enemies: ['Sun', 'Moon', 'Mars'], neutral: ['Jupiter'] }
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：岁差计算 (Lahiri Ayanamsa)
  // ════════════════════════════════════════════════════════════════════════════

  getAyanamsa(year) {
    // Lahiri Ayanamsa 线性近似
    // 精度：±0.1° (对大多数应用足够)
    return 22.46 + (year - 1900) * 0.01397;
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：热带 → 恒星转换
  // ════════════════════════════════════════════════════════════════════════════

  toSidereal(tropicalLongitude, year) {
    const ayanamsa = this.getAyanamsa(year);
    let sidereal = tropicalLongitude - ayanamsa;
    if (sidereal < 0) sidereal += 360;
    if (sidereal >= 360) sidereal -= 360;
    return sidereal;
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：获取星座
  // ════════════════════════════════════════════════════════════════════════════

  getSign(longitude) {
    const normalized = ((longitude % 360) + 360) % 360;
    const index = Math.floor(normalized / 30);
    const degree = Math.floor(normalized % 30);
    const minute = Math.floor((normalized % 1) * 60);
    
    return {
      ...this.SIGNS[index],
      degree: degree,
      minute: minute,
      longitude: normalized
    };
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：获取月宿
  // ════════════════════════════════════════════════════════════════════════════

  getNakshatra(longitude) {
    const normalized = ((longitude % 360) + 360) % 360;
    const nakshatraSpan = 13.333333333; // 13° 20'
    const index = Math.floor(normalized / nakshatraSpan);
    const positionInNakshatra = normalized % nakshatraSpan;
    const percentage = (positionInNakshatra / nakshatraSpan) * 100;
    const pada = Math.floor(percentage / 25) + 1; // 每个月宿4个 pada
    
    return {
      ...this.NAKSHATRAS[index],
      pada: pada,
      percentage: percentage,
      longitude: normalized
    };
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：计算月球交点 (Rahu/Ketu)
  // ════════════════════════════════════════════════════════════════════════════

  calculateLunarNodes(date) {
    // 平均北交点计算
    const J2000 = new Date('2000-01-01T12:00:00Z');
    const daysSinceJ2000 = (date - J2000) / (1000 * 60 * 60 * 24);
    
    // Rahu 在 J2000 的平均经度：~125.04°
    // 每日运动：-0.05295°（逆行）
    let rahu = 125.04 - (0.05295 * daysSinceJ2000);
    rahu = ((rahu % 360) + 360) % 360;
    
    let ketu = (rahu + 180) % 360;
    
    return { rahu, ketu };
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：计算宫位
  // ════════════════════════════════════════════════════════════════════════════

  calculateHouse(planetLongitude, ascendantLongitude) {
    let diff = planetLongitude - ascendantLongitude;
    if (diff < 0) diff += 360;
    return Math.floor(diff / 30) + 1;
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：计算大运 (Vimshottari Dasha)
  // ════════════════════════════════════════════════════════════════════════════

  calculateDasha(moonNakshatra, birthDate, targetDate = new Date()) {
    const lordIndex = this.DASHA_SEQUENCE.indexOf(moonNakshatra.lord);
    
    // 计算出生时第一个大运的剩余时间
    const elapsedPercentage = moonNakshatra.percentage;
    const remainingPercentage = 100 - elapsedPercentage;
    const firstDashaYears = this.DASHA_YEARS[moonNakshatra.lord];
    const firstDashaRemaining = (remainingPercentage / 100) * firstDashaYears;
    
    // 构建大运时间线
    const timeline = [];
    let runningDate = new Date(birthDate);
    
    // 第一个（部分）大运
    let endDate = this.addYearsToDate(runningDate, firstDashaRemaining);
    timeline.push({
      planet: moonNakshatra.lord,
      planetData: this.PLANETS[moonNakshatra.lord],
      start: new Date(runningDate),
      end: endDate,
      years: firstDashaRemaining,
      isPartial: true
    });
    runningDate = new Date(endDate);
    
    // 后续完整大运（循环）
    for (let i = 1; i <= 9; i++) {
      const idx = (lordIndex + i) % 9;
      const planet = this.DASHA_SEQUENCE[idx];
      const years = this.DASHA_YEARS[planet];
      
      endDate = this.addYearsToDate(runningDate, years);
      timeline.push({
        planet: planet,
        planetData: this.PLANETS[planet],
        start: new Date(runningDate),
        end: endDate,
        years: years,
        isPartial: false
      });
      runningDate = new Date(endDate);
    }
    
    // 找到当前大运
    const currentDasha = timeline.find(d => targetDate >= d.start && targetDate < d.end);
    
    // 计算当前大运的进度
    let progress = 0;
    if (currentDasha) {
      const totalMs = currentDasha.end - currentDasha.start;
      const elapsedMs = targetDate - currentDasha.start;
      progress = (elapsedMs / totalMs) * 100;
    }
    
    // 计算子大运 (Antardasha)
    let antardasha = null;
    if (currentDasha) {
      antardasha = this.calculateAntardasha(currentDasha, targetDate);
    }
    
    return {
      timeline: timeline,
      current: currentDasha,
      progress: progress,
      antardasha: antardasha
    };
  },

  // 计算子大运 (Antardasha)
  calculateAntardasha(mahadasha, targetDate) {
    const startIndex = this.DASHA_SEQUENCE.indexOf(mahadasha.planet);
    const totalYears = mahadasha.years;
    const totalDays = totalYears * 365.25;
    
    const antardashas = [];
    let runningDate = new Date(mahadasha.start);
    
    for (let i = 0; i < 9; i++) {
      const idx = (startIndex + i) % 9;
      const planet = this.DASHA_SEQUENCE[idx];
      const planetYears = this.DASHA_YEARS[planet];
      
      // Antardasha duration = (Mahadasha years * Planet years) / 120
      const antarYears = (totalYears * planetYears) / 120;
      const endDate = this.addYearsToDate(runningDate, antarYears);
      
      antardashas.push({
        planet: planet,
        planetData: this.PLANETS[planet],
        start: new Date(runningDate),
        end: endDate,
        years: antarYears
      });
      runningDate = new Date(endDate);
    }
    
    const current = antardashas.find(a => targetDate >= a.start && targetDate < a.end);
    
    return {
      timeline: antardashas,
      current: current
    };
  },

  addYearsToDate(date, years) {
    const result = new Date(date);
    const wholeYears = Math.floor(years);
    const fractionalDays = (years - wholeYears) * 365.25;
    
    result.setFullYear(result.getFullYear() + wholeYears);
    result.setDate(result.getDate() + Math.floor(fractionalDays));
    
    return result;
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：检测 Manglik Dosha（火星缺陷）
  // ════════════════════════════════════════════════════════════════════════════

  checkManglik(chart) {
    const marsHouse = chart.planets.Mars.house;
    // 火星在 1, 2, 4, 7, 8, 12 宫为 Manglik
    const manglikHouses = [1, 2, 4, 7, 8, 12];
    
    const isManglik = manglikHouses.includes(marsHouse);
    
    let severity = 'None';
    let description = '';
    
    if (isManglik) {
      if ([1, 4, 7, 8].includes(marsHouse)) {
        severity = 'High';
        description = `Mars in ${marsHouse}th house indicates strong Manglik Dosha.`;
      } else {
        severity = 'Mild';
        description = `Mars in ${marsHouse}th house indicates mild Manglik Dosha.`;
      }
    }
    
    return {
      isManglik: isManglik,
      house: marsHouse,
      severity: severity,
      description: description,
      descriptionCn: isManglik 
        ? `火星在第${marsHouse}宫，存在${severity === 'High' ? '严重' : '轻微'}火星缺陷。` 
        : '无火星缺陷。'
    };
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 核心函数：婚配计算 (Gun Milan - Ashtakoot)
  // ════════════════════════════════════════════════════════════════════════════

  calculateGunMilan(chartA, chartB) {
    const nakshatraA = chartA.planets.Moon.nakshatra;
    const nakshatraB = chartB.planets.Moon.nakshatra;
    
    const results = {
      total: 0,
      maxTotal: 36,
      aspects: []
    };
    
    // 1. Varna (精神契合) - 1 point
    const varnaScore = this.calculateVarna(nakshatraA, nakshatraB);
    results.aspects.push(varnaScore);
    results.total += varnaScore.score;
    
    // 2. Vashya (相互吸引) - 2 points
    const vashyaScore = this.calculateVashya(chartA, chartB);
    results.aspects.push(vashyaScore);
    results.total += vashyaScore.score;
    
    // 3. Tara (命运和谐) - 3 points
    const taraScore = this.calculateTara(nakshatraA, nakshatraB);
    results.aspects.push(taraScore);
    results.total += taraScore.score;
    
    // 4. Yoni (亲密兼容) - 4 points
    const yoniScore = this.calculateYoni(nakshatraA, nakshatraB);
    results.aspects.push(yoniScore);
    results.total += yoniScore.score;
    
    // 5. Graha Maitri (心智连接) - 5 points
    const grahaScore = this.calculateGrahaMaitri(chartA, chartB);
    results.aspects.push(grahaScore);
    results.total += grahaScore.score;
    
    // 6. Gana (性情) - 6 points
    const ganaScore = this.calculateGana(nakshatraA, nakshatraB);
    results.aspects.push(ganaScore);
    results.total += ganaScore.score;
    
    // 7. Bhakoot (财务/家庭) - 7 points
    const bhakootScore = this.calculateBhakoot(chartA, chartB);
    results.aspects.push(bhakootScore);
    results.total += bhakootScore.score;
    
    // 8. Nadi (健康/后代) - 8 points
    const nadiScore = this.calculateNadi(nakshatraA, nakshatraB);
    results.aspects.push(nadiScore);
    results.total += nadiScore.score;
    
    // 评级
    if (results.total >= 32) {
      results.rating = 'Excellent';
      results.ratingCn = '极佳';
    } else if (results.total >= 24) {
      results.rating = 'Good';
      results.ratingCn = '良好';
    } else if (results.total >= 18) {
      results.rating = 'Average';
      results.ratingCn = '一般';
    } else {
      results.rating = 'Below Average';
      results.ratingCn = '较差';
    }
    
    // Manglik 检测
    results.manglikA = this.checkManglik(chartA);
    results.manglikB = this.checkManglik(chartB);
    
    return results;
  },

  // Gun Milan 子函数
  calculateVarna(nakshatraA, nakshatraB) {
    const varnaOrder = { Brahmin: 4, Kshatriya: 3, Vaishya: 2, Shudra: 1, Mleccha: 0 };
    const varnaA = varnaOrder[nakshatraA.varna] || 0;
    const varnaB = varnaOrder[nakshatraB.varna] || 0;
    
    const score = varnaA >= varnaB ? 1 : 0;
    
    return {
      name: 'Varna',
      nameCn: '种姓',
      description: 'Spiritual compatibility',
      descriptionCn: '精神契合度',
      maxScore: 1,
      score: score,
      details: `${nakshatraA.varna} & ${nakshatraB.varna}`,
      detailsCn: score === 1 ? '精神层面和谐' : '精神层面存在差异'
    };
  },

  calculateVashya(chartA, chartB) {
    // 简化版：基于星座兼容性
    const signA = chartA.planets.Moon.sign.index;
    const signB = chartB.planets.Moon.sign.index;
    
    // 友好星座组合得高分
    const diff = Math.abs(signA - signB);
    let score = 0;
    
    if (diff === 0) score = 2;          // 同星座
    else if ([4, 8].includes(diff)) score = 2;  // 三合
    else if ([3, 9].includes(diff)) score = 1.5; // 四合
    else if ([2, 10].includes(diff)) score = 1;
    else score = 0.5;
    
    return {
      name: 'Vashya',
      nameCn: '互吸',
      description: 'Mutual attraction',
      descriptionCn: '相互吸引力',
      maxScore: 2,
      score: score,
      details: 'Based on Moon sign compatibility',
      detailsCn: score >= 1.5 ? '强烈的相互吸引' : '吸引力一般'
    };
  },

  calculateTara(nakshatraA, nakshatraB) {
    const indexA = nakshatraA.index;
    const indexB = nakshatraB.index;
    
    // Tara = (B - A) mod 9
    let tara = ((indexB - indexA) % 9 + 9) % 9;
    if (tara === 0) tara = 9;
    
    // 有利 Tara: 1, 2, 4, 6, 8, 9
    const favorable = [1, 2, 4, 6, 8, 9];
    const mutualTara = ((indexA - indexB) % 9 + 9) % 9 || 9;
    
    let score = 0;
    if (favorable.includes(tara) && favorable.includes(mutualTara)) score = 3;
    else if (favorable.includes(tara) || favorable.includes(mutualTara)) score = 1.5;
    else score = 0;
    
    return {
      name: 'Tara',
      nameCn: '命星',
      description: 'Destiny compatibility',
      descriptionCn: '命运和谐度',
      maxScore: 3,
      score: score,
      details: `Tara positions: ${tara} & ${mutualTara}`,
      detailsCn: score === 3 ? '命运高度和谐' : score >= 1.5 ? '命运部分和谐' : '命运存在挑战'
    };
  },

  calculateYoni(nakshatraA, nakshatraB) {
    const yoniA = nakshatraA.yoni;
    const yoniB = nakshatraB.yoni;
    
    // Yoni 兼容性矩阵（简化版）
    const yoniPairs = {
      'Horse-Horse': 4, 'Elephant-Elephant': 4, 'Sheep-Sheep': 4,
      'Serpent-Serpent': 4, 'Dog-Dog': 4, 'Cat-Cat': 4,
      'Rat-Rat': 4, 'Cow-Cow': 4, 'Buffalo-Buffalo': 4,
      'Tiger-Tiger': 4, 'Deer-Deer': 4, 'Monkey-Monkey': 4,
      'Lion-Lion': 4, 'Mongoose-Mongoose': 4,
      // 敌对
      'Horse-Buffalo': 0, 'Buffalo-Horse': 0,
      'Elephant-Lion': 0, 'Lion-Elephant': 0,
      'Dog-Deer': 0, 'Deer-Dog': 0,
      'Cat-Rat': 0, 'Rat-Cat': 0,
      'Serpent-Mongoose': 0, 'Mongoose-Serpent': 0,
      'Monkey-Sheep': 0, 'Sheep-Monkey': 0,
      'Cow-Tiger': 0, 'Tiger-Cow': 0
    };
    
    const key = `${yoniA}-${yoniB}`;
    const reverseKey = `${yoniB}-${yoniA}`;
    
    let score = yoniPairs[key] ?? yoniPairs[reverseKey] ?? 2; // 默认中等
    
    return {
      name: 'Yoni',
      nameCn: '性合',
      description: 'Physical/intimate compatibility',
      descriptionCn: '亲密兼容度',
      maxScore: 4,
      score: score,
      details: `${yoniA} & ${yoniB}`,
      detailsCn: score === 4 ? '高度亲密和谐' : score >= 2 ? '亲密关系良好' : '亲密方面存在挑战'
    };
  },

  calculateGrahaMaitri(chartA, chartB) {
    const lordA = chartA.planets.Moon.sign.lord;
    const lordB = chartB.planets.Moon.sign.lord;
    
    if (lordA === lordB) {
      return {
        name: 'Graha Maitri',
        nameCn: '心智',
        description: 'Mental compatibility',
        descriptionCn: '心智连接',
        maxScore: 5,
        score: 5,
        details: `Same lord: ${lordA}`,
        detailsCn: '同一主星——深度心灵相通'
      };
    }
    
    const relationsA = this.PLANET_RELATIONS[lordA] || {};
    const relationsB = this.PLANET_RELATIONS[lordB] || {};
    
    const aToB = relationsA.friends?.includes(lordB) ? 'friend' : 
                 relationsA.enemies?.includes(lordB) ? 'enemy' : 'neutral';
    const bToA = relationsB.friends?.includes(lordA) ? 'friend' : 
                 relationsB.enemies?.includes(lordA) ? 'enemy' : 'neutral';
    
    let score = 0;
    if (aToB === 'friend' && bToA === 'friend') score = 5;
    else if (aToB === 'friend' || bToA === 'friend') score = 4;
    else if (aToB === 'neutral' && bToA === 'neutral') score = 3;
    else if (aToB === 'enemy' && bToA === 'enemy') score = 0;
    else score = 1;
    
    return {
      name: 'Graha Maitri',
      nameCn: '心智',
      description: 'Mental compatibility',
      descriptionCn: '心智连接',
      maxScore: 5,
      score: score,
      details: `${lordA} (${aToB}) ↔ ${lordB} (${bToA})`,
      detailsCn: score >= 4 ? '深度心灵理解' : score >= 2 ? '心智沟通良好' : '心智层面存在障碍'
    };
  },

  calculateGana(nakshatraA, nakshatraB) {
    const ganaA = nakshatraA.gana;
    const ganaB = nakshatraB.gana;
    
    // Gana 兼容性
    // Deva-Deva: 6, Manush-Manush: 6, Raksh-Raksh: 6
    // Deva-Manush: 6, Manush-Deva: 5
    // Deva-Raksh: 1, Raksh-Deva: 0
    // Manush-Raksh: 3, Raksh-Manush: 1
    
    const ganaScores = {
      'Deva-Deva': 6, 'Manush-Manush': 6, 'Raksh-Raksh': 6,
      'Deva-Manush': 6, 'Manush-Deva': 5,
      'Deva-Raksh': 1, 'Raksh-Deva': 0,
      'Manush-Raksh': 3, 'Raksh-Manush': 1
    };
    
    const key = `${ganaA}-${ganaB}`;
    const score = ganaScores[key] ?? 3;
    
    return {
      name: 'Gana',
      nameCn: '性情',
      description: 'Temperament match',
      descriptionCn: '性情匹配',
      maxScore: 6,
      score: score,
      details: `${ganaA} & ${ganaB}`,
      detailsCn: score >= 5 ? '性情高度相合' : score >= 3 ? '性情基本相合' : '性情差异较大'
    };
  },

  calculateBhakoot(chartA, chartB) {
    const signA = chartA.planets.Moon.sign.index;
    const signB = chartB.planets.Moon.sign.index;
    
    const diff = Math.abs(signA - signB);
    const position = Math.min(diff, 12 - diff);
    
    // 不利位置：2-12, 5-9, 6-8
    const unfavorable = [2, 4, 6]; // 简化：2, 5-1=4, 6
    
    let score = 7;
    if (unfavorable.includes(position)) {
      score = 0;
    }
    
    return {
      name: 'Bhakoot',
      nameCn: '财运',
      description: 'Financial/family harmony',
      descriptionCn: '财务与家庭和谐',
      maxScore: 7,
      score: score,
      details: `Position difference: ${position}`,
      detailsCn: score === 7 ? '财务家庭和谐' : '可能存在财务或家庭摩擦'
    };
  },

  calculateNadi(nakshatraA, nakshatraB) {
    const nadiA = nakshatraA.nadi;
    const nadiB = nakshatraB.nadi;
    
    // 同 Nadi = 0（最不利，影响后代健康）
    // 不同 Nadi = 8（最有利）
    const score = nadiA === nadiB ? 0 : 8;
    
    return {
      name: 'Nadi',
      nameCn: '脉象',
      description: 'Health & progeny',
      descriptionCn: '健康与后代',
      maxScore: 8,
      score: score,
      details: `${nadiA} & ${nadiB}`,
      detailsCn: score === 8 ? '不同脉象——后代健康' : '相同脉象——需要补救措施'
    };
  },

  // ════════════════════════════════════════════════════════════════════════════
  // 主函数：计算完整吠陀星盘
  // ════════════════════════════════════════════════════════════════════════════

  calculateVedicChart(year, month, day, hour, minute, latitude, longitude, timezone) {
    // 构建日期时间
    const date = new Date(year, month - 1, day, hour, minute);
    const utcDate = new Date(date.getTime() - timezone * 60 * 60 * 1000);
    
    // 使用 Astronomy.js 获取热带经度
    const tropicalPositions = {};
    const planetIds = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
    
    planetIds.forEach(planet => {
      try {
        const ecliptic = Astronomy.EclipticGeoMoon ? 
          (planet === 'Moon' ? Astronomy.EclipticGeoMoon(utcDate) : Astronomy.GeoVector(planet, utcDate, true)) :
          Astronomy.GeoVector(planet, utcDate, true);
        
        // 转换为黄道经度
        let lon;
        if (planet === 'Moon' && Astronomy.EclipticGeoMoon) {
          lon = Astronomy.EclipticGeoMoon(utcDate).lon;
        } else {
          const equ = Astronomy.Equator(planet, utcDate, null, false, true);
          const ecl = Astronomy.Ecliptic(equ.vec);
          lon = ecl.elon;
        }
        
        tropicalPositions[planet] = lon;
      } catch (e) {
        // 备用计算
        console.warn(`Astronomy.js error for ${planet}, using fallback`);
        tropicalPositions[planet] = this.fallbackPlanetPosition(planet, utcDate);
      }
    });
    
    // 计算月球交点
    const nodes = this.calculateLunarNodes(utcDate);
    tropicalPositions.Rahu = nodes.rahu;
    tropicalPositions.Ketu = nodes.ketu;
    
    // 计算上升点（如果有精确时间）
    let ascendantLongitude = null;
    const hasExactTime = !(hour === 12 && minute === 0); // 假设中午12:00为默认值
    
    if (hasExactTime) {
      ascendantLongitude = this.calculateAscendant(utcDate, latitude, longitude);
    }
    
    // 转换为恒星坐标并构建行星数据
    const planets = {};
    const ayanamsa = this.getAyanamsa(year);
    
    Object.entries(tropicalPositions).forEach(([planet, tropical]) => {
      const sidereal = this.toSidereal(tropical, year);
      const sign = this.getSign(sidereal);
      const nakshatra = this.getNakshatra(sidereal);
      const house = ascendantLongitude ? this.calculateHouse(sidereal, ascendantLongitude) : null;
      
      planets[planet] = {
        ...this.PLANETS[planet],
        tropical: tropical,
        sidereal: sidereal,
        sign: sign,
        nakshatra: nakshatra,
        house: house,
        degree: sign.degree,
        minute: sign.minute
      };
    });
    
    // 上升星座
    let ascendant = null;
    if (ascendantLongitude) {
      const siderealAsc = this.toSidereal(ascendantLongitude, year);
      ascendant = {
        tropical: ascendantLongitude,
        sidereal: siderealAsc,
        sign: this.getSign(siderealAsc),
        nakshatra: this.getNakshatra(siderealAsc)
      };
    }
    
    // 计算大运
    let dasha = null;
    if (planets.Moon) {
      dasha = this.calculateDasha(planets.Moon.nakshatra, date);
    }
    
    // 获取补救措施（基于当前大运）
    let remedies = null;
    if (dasha && dasha.current) {
      remedies = this.REMEDIES[dasha.current.planet];
    }
    
    // 三巨头
    const sunSign = planets.Sun.sign.nameEn;
    const moonSign = planets.Moon.sign.nameEn;
    const risingSign = ascendant ? ascendant.sign.nameEn : null;
    
    return {
      birthDate: date,
      ayanamsa: ayanamsa,
      hasExactTime: hasExactTime,
      
      // 三巨头
      sunSign: sunSign,
      sunSignCn: planets.Sun.sign.nameCn,
      moonSign: moonSign,
      moonSignCn: planets.Moon.sign.nameCn,
      risingSign: risingSign,
      risingSignCn: ascendant ? ascendant.sign.nameCn : null,
      
      // 月宿
      moonNakshatra: planets.Moon.nakshatra,
      
      // 行星
      planets: planets,
      
      // 上升
      ascendant: ascendant,
      
      // 大运
      dasha: dasha,
      
      // 补救措施
      remedies: remedies,
      
      // Manglik
      manglik: hasExactTime ? this.checkManglik({ planets }) : null
    };
  },

  // 计算上升点
  calculateAscendant(utcDate, latitude, longitude) {
    try {
      // 使用 Astronomy.js 的恒星时
      const lst = Astronomy.SiderealTime(utcDate);
      
      // 简化计算（更精确需要考虑黄道倾角）
      const obliquity = 23.44; // 黄道倾角
      const latRad = latitude * Math.PI / 180;
      const oblRad = obliquity * Math.PI / 180;
      
      // 本地恒星时转度数
      const lstDeg = (lst * 15 + longitude) % 360;
      
      // 简化的上升点计算
      const ascRad = Math.atan2(
        Math.cos(lstDeg * Math.PI / 180),
        -(Math.sin(lstDeg * Math.PI / 180) * Math.cos(oblRad) + Math.tan(latRad) * Math.sin(oblRad))
      );
      
      let asc = ascRad * 180 / Math.PI;
      if (asc < 0) asc += 360;
      
      return asc;
    } catch (e) {
      console.warn('Ascendant calculation error:', e);
      return null;
    }
  },

  // 备用行星位置计算（简化）
  fallbackPlanetPosition(planet, date) {
    // 非常简化的平均位置计算
    const J2000 = new Date('2000-01-01T12:00:00Z');
    const days = (date - J2000) / (1000 * 60 * 60 * 24);
    
    const meanMotions = {
      Sun: 0.9856474,
      Moon: 13.1763966,
      Mercury: 4.0923344,
      Venus: 1.6021302,
      Mars: 0.5240208,
      Jupiter: 0.0830853,
      Saturn: 0.0334442
    };
    
    const J2000Positions = {
      Sun: 280.46,
      Moon: 218.32,
      Mercury: 252.25,
      Venus: 181.98,
      Mars: 355.45,
      Jupiter: 34.40,
      Saturn: 50.08
    };
    
    const motion = meanMotions[planet] || 1;
    const startPos = J2000Positions[planet] || 0;
    
    return ((startPos + motion * days) % 360 + 360) % 360;
  }
};

// 导出
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VedicCalculator;
}